import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import {
  annotationMutationMetaSchema,
  categoryAssignmentSchema,
  operationIdSchema,
  parseMutationFailure,
  publicLineItemSchema,
  type AnnotationChanges,
  type MutationResult,
} from './useLineItemPatch';

const uuidSchema = z.string().uuid();
const positiveIntSchema = z.number().int().positive();
const splitMoneySchema = z.string()
  .regex(/^-?(0|[1-9][0-9]{0,14})\.[0-9]{4}$/)
  .refine((value) => value !== '0.0000' && value !== '-0.0000', 'split values must be non-zero');
const monthSchema = z.string().regex(/^[0-9]{4}-[0-9]{2}$/);

export const expectedLineItemVersionSchema = z.object({
  line_item_id: uuidSchema,
  version: positiveIntSchema,
}).strict();
export type ExpectedLineItemVersion = z.infer<typeof expectedLineItemVersionSchema>;

export const splitPartSchema = z.object({
  client_part_id: uuidSchema,
  amount: splitMoneySchema,
  category: categoryAssignmentSchema.nullable().optional(),
  business_state: z.enum(['business', 'personal']).nullable().optional(),
  allocation_month: monthSchema.nullable().optional(),
  allocation_percent: splitMoneySchema.nullable().optional(),
}).strict();
export type SplitPart = z.infer<typeof splitPartSchema>;

export const splitModeSchema = z.enum(['amount', 'percent', 'categories', 'months']);

export const splitTransactionRequestSchema = z.object({
  operation_id: operationIdSchema,
  expected_revision: positiveIntSchema,
  expected_line_items: z.array(expectedLineItemVersionSchema).min(1).max(50),
  mode: splitModeSchema,
  parts: z.array(splitPartSchema).min(1).max(50),
}).strict().superRefine((request, context) => {
  const partIds = request.parts.map((part) => part.client_part_id);
  if (new Set(partIds).size !== partIds.length) {
    context.addIssue({ code: 'custom', path: ['parts'], message: 'client_part_id must be unique' });
  }
  const expectedIds = request.expected_line_items.map((item) => item.line_item_id);
  if (new Set(expectedIds).size !== expectedIds.length) {
    context.addIssue({ code: 'custom', path: ['expected_line_items'], message: 'expected line-item IDs must be unique' });
  }
  const negative = request.parts[0]?.amount.startsWith('-');
  request.parts.forEach((part, index) => {
    if (part.amount.startsWith('-') !== negative) {
      context.addIssue({ code: 'custom', path: ['parts', index, 'amount'], message: 'split parts must have the same sign' });
    }
    if (request.mode === 'amount' || request.mode === 'categories') {
      if (part.allocation_month !== undefined && part.allocation_month !== null) {
        context.addIssue({ code: 'custom', path: ['parts', index, 'allocation_month'], message: 'months are only valid in months mode' });
      }
      if (part.allocation_percent !== undefined && part.allocation_percent !== null) {
        context.addIssue({ code: 'custom', path: ['parts', index, 'allocation_percent'], message: 'percentages are only valid in percent mode' });
      }
    }
    if (request.mode === 'percent') {
      if (part.allocation_percent === undefined || part.allocation_percent === null) {
        context.addIssue({ code: 'custom', path: ['parts', index, 'allocation_percent'], message: 'percent mode requires allocation_percent' });
      }
      if (part.allocation_month !== undefined && part.allocation_month !== null) {
        context.addIssue({ code: 'custom', path: ['parts', index, 'allocation_month'], message: 'months are only valid in months mode' });
      }
    }
    if (request.mode === 'months' && (part.allocation_month === undefined || part.allocation_month === null)) {
      context.addIssue({ code: 'custom', path: ['parts', index, 'allocation_month'], message: 'months mode requires allocation_month' });
    }
    if (request.mode === 'months' && part.allocation_percent !== undefined && part.allocation_percent !== null) {
      context.addIssue({ code: 'custom', path: ['parts', index, 'allocation_percent'], message: 'percentages are only valid in percent mode' });
    }
    if (request.mode === 'categories' && (part.category === undefined || part.category === null)) {
      context.addIssue({ code: 'custom', path: ['parts', index, 'category'], message: 'categories mode requires a category' });
    }
  });
  if (request.mode === 'months') {
    const months = request.parts.map((part) => part.allocation_month).filter((month): month is string => month !== undefined && month !== null);
    if (new Set(months).size !== months.length || months.some((month, index) => index > 0 && month <= months[index - 1])) {
      context.addIssue({ code: 'custom', path: ['parts'], message: 'months must be unique and strictly ascending' });
    }
  }
});
export type SplitTransactionRequest = z.infer<typeof splitTransactionRequestSchema>;

export const splitTransactionDataSchema = z.object({
  transaction_id: uuidSchema,
  replaced_line_item_ids: z.array(uuidSchema),
  line_items: z.array(publicLineItemSchema),
  batch_id: uuidSchema,
  audit_ids: z.array(positiveIntSchema),
  mode: splitModeSchema,
}).strict();
export type SplitTransactionData = z.infer<typeof splitTransactionDataSchema>;
export const splitTransactionResponseSchema = z.object({
  data: splitTransactionDataSchema,
  meta: annotationMutationMetaSchema,
}).strict();

type SplitMutationInput = {
  transactionId: string;
  input: Omit<SplitTransactionRequest, 'operation_id'> & { operation_id?: string };
};

function generatedOperationId(): string {
  const generated = globalThis.crypto?.randomUUID?.();
  if (!generated) throw new Error('operation ID generation is unavailable');
  return operationIdSchema.parse(generated);
}

export function useSplitTransaction() {
  return useMutation<MutationResult<SplitTransactionData>, Error, SplitMutationInput>({
    mutationFn: async ({ transactionId, input }) => {
      const body = splitTransactionRequestSchema.parse({ ...input, operation_id: input.operation_id ?? generatedOperationId() });
      uuidSchema.parse(transactionId);
      try {
        const response = await client.post(`/api/transactions/${transactionId}/split`, body);
        const parsed = parseEnvelope(splitTransactionDataSchema, response.data);
        const meta = annotationMutationMetaSchema.parse(parsed.meta ?? {});
        return meta.idempotent_replay
          ? { kind: 'replay', idempotent_replay: true, data: parsed.data }
          : { kind: 'success', idempotent_replay: false, data: parsed.data };
      } catch (error) {
        if (isAxiosError(error)) return parseMutationFailure(error);
        throw error;
      }
    },
    retry: false,
  });
}

export type SplitAnnotationChanges = AnnotationChanges;
